import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.cam';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.metaDescription,
        url: `${siteUrl}/artigo/${article.slug}`,
        image: article.imageUrl,
        inLanguage: 'pt',
        datePublished: '2025-01-15',
        dateModified: '2025-06-25',
        author: { '@type': 'Organization', name: 'Hoobuy.cam', url: `${siteUrl}/` },
        publisher: { '@type': 'Organization', name: 'Hoobuy.cam', url: `${siteUrl}/`, logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg' } },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/artigo/${article.slug}` },
      }
    : null;

  const breadcrumbJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Início', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: article.title, item: `${siteUrl}/artigo/${article.slug}` },
        ],
      }
    : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Artigo não encontrado</h1>
            <p className="text-foreground-600 mb-8">O artigo que você procura não está disponível.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              Voltar ao Início
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {articleJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />
      )}
      {breadcrumbJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
      )}

      <Navbar />

      <main>
        <article className="pt-24 pb-10">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-xs text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">
                    Início
                  </Link>
                </li>
                <li className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li className="text-foreground-700">{article.title}</li>
              </ol>
            </nav>

            <div className="mb-8">
              <p className="text-xs text-foreground-500 font-medium tracking-widest uppercase mb-4">
                {article.category}
              </p>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-6 text-center">
                {article.title}
              </h1>

              <div className="rounded-xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-[200px] md:h-[340px] object-cover object-top"
                />
              </div>

              <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-8">
                {article.sections.map((section, sIdx) => (
                  <section key={sIdx}>
                    <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} className="mb-4" dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                  </section>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 pt-8 border-t border-background-200/60">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </span>
                Página Inicial
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Acessar Hoobuy Sheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
            </div>
          </div>
        </article>

        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10">
              <p className="text-xs text-foreground-500 font-medium tracking-widest uppercase mb-2">
                Continue Explorando
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight">
                Artigos{' '}
                <span className="italic font-normal text-accent-600">Relacionados</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {articles
                .filter((a) => a.slug !== slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/artigo/${a.slug}`}
                    className="group cursor-pointer rounded-xl overflow-hidden transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="bg-background-50 aspect-[4/5] relative overflow-hidden">
                      <img
                        src={a.imageUrl}
                        alt={a.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-xs text-white/70 uppercase tracking-wider mb-1">{a.category}</p>
                        <p className="text-sm font-bold text-white leading-tight drop-shadow-md line-clamp-2">{a.title}</p>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}